import React, { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ArrowIcon from '@assets/images/arrow-right.png'
import { useMappedAttendeesProfiles } from '@components/Attendees'
import { ContactIcon } from '@components/Attendees/index.styles'
import CardsList from '@components/CardsList'
import { CustomTooltip } from '@components/CustomTooltip'
import TruncateLines from '@components/TruncateLines'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import useHasContactedMap from '@modules/chat/hooks/useHasContactedMap'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import { MEETING_STATUS_TO_COLOR_MAP } from '@modules/contacts/constants'
import { CompanyContactHistoryCounts, Meeting, MeetingStatus } from '@modules/contacts/types'
import { UpdatedLabel } from '@modules/profile/index.styles'
import { AttendeeItem } from '@modules/profile/types'
import { Delegate, ProfileType } from '@modules/search/types'
import ChatIcon from '@mui/icons-material/Chat'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import LockIcon from '@mui/icons-material/Lock'
import { Button, Radio } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { colors } from '@theme/variables'

import ContactCounts from './ContactCounts'
import MeetingData from './MeetingData'
import {
  AttendeeInfoContainer,
  AttendeesArrow,
  AttendeesContainer,
  ContactCountsWrapper,
  ContactStatusBar,
  ContactsOverviewCardContent,
  ContactsOverviewCardLockIcon,
  ContactsOverviewCardWrapper,
  ContactsRadioWrapper,
  DetailsButtonWrapper,
} from './index.styles'

interface ContactsOverviewBoxProps {
  companyId: number
  counts?: Nullable<CompanyContactHistoryCounts>
  meeting?: Nullable<Meeting>
  onDetailsClick?: React.MouseEventHandler<HTMLDivElement>
  hideDetailsBtn?: boolean
  showSelection?: boolean
  isSelected?: boolean
  setSelected?: (meeting: Meeting) => void
  singleViewMode?: boolean
  isHighlighted?: boolean
}

const ITEM_ATTENDEE_WIDTH = 32

const ContactsOverviewCard = (props: ContactsOverviewBoxProps) => {
  const {
    companyId,
    counts,
    meeting,
    hideDetailsBtn = false,
    onDetailsClick,
    showSelection,
    isSelected,
    setSelected,
    singleViewMode = false,
    isHighlighted,
  } = props
  const { t } = useTranslation()
  const { openChatModal } = useChatContext()
  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat } = useFeatureFlags()

  const [lockIconTooltipShown, setLockIconTooltipShown] = useState<boolean>(false)

  const meetingStatus = meeting?.status as MeetingStatus
  const isUpcomingMeeting = meetingStatus === MeetingStatus.UPCOMING

  const companyAttendees = useMappedAttendeesProfiles(meeting?.attendees?.company ?? [])
  const otherCompanyAttendees = useMappedAttendeesProfiles(meeting?.attendees?.otherCompany ?? [])

  const allAttendees = [...(meeting?.attendees?.company ?? []), ...(meeting?.attendees?.otherCompany ?? [])]

  const companyContactsMap = useHasContactedMap({
    profileIds: [...allAttendees.map(i => ({ id: i.id, type: ProfileType.delegate }))],
  })

  if (!meeting && !counts) {
    return null
  }

  const isMeetingDataDisplayed = hideDetailsBtn || (!hideDetailsBtn && isUpcomingMeeting)

  const isImported = meeting?.event?.imported
  const isPrivateProfile = meeting?.otherCompanyPrivate

  const renderContactIcon = (item: AttendeeItem, isTooltipView?: boolean) => (
    <Box>
      {item.id !== user?.id && hasAccessToChat && !isEcommerce && (
        <Box mt="6px">
          <ContactedBubble hasContacted={companyContactsMap?.[ProfileType.delegate]?.[item.id]}>
            {isTooltipView ? (
              <ContactIcon
                onClick={() =>
                  openChatModal({ delegates: [item] as unknown as Delegate[], companyId, chatType: ChatType.DIRECT })
                }
              >
                <ChatIcon fontSize="small" />
              </ContactIcon>
            ) : (
              <Button
                variant="contained"
                data-testid="contact_btn"
                onClick={() =>
                  openChatModal({ delegates: [item] as unknown as Delegate[], companyId, chatType: ChatType.DIRECT })
                }
              >
                {t(translationKeys.CONTACT_BUTTON)}
              </Button>
            )}
          </ContactedBubble>
        </Box>
      )}
    </Box>
  )

  return (
    <ContactsOverviewCardWrapper
      $singleViewMode={singleViewMode}
      $isHighlighted={isHighlighted}
      data-testid="contact-box-wrapper"
    >
      {!!isHighlighted && <UpdatedLabel isAbsolute>{t(translationKeys.NEW_ACTIVITY)}</UpdatedLabel>}
      <ContactStatusBar $color={meeting ? MEETING_STATUS_TO_COLOR_MAP[meetingStatus]?.bar : undefined} />
      <ContactsOverviewCardContent>
        {meeting && isMeetingDataDisplayed ? (
          <MeetingData singleViewMode={singleViewMode} meeting={meeting} isCountsOverview={Boolean(counts)} />
        ) : null}

        {counts ? (
          <ContactCountsWrapper>
            <ContactCounts counts={counts} />
          </ContactCountsWrapper>
        ) : (
          !!meeting?.attendees && (
            <AttendeesContainer $singleViewMode={singleViewMode}>
              <Box display="flex">
                <Box minWidth={ITEM_ATTENDEE_WIDTH}>
                  <CardsList
                    cardsItems={otherCompanyAttendees}
                    totalCardsAmount={otherCompanyAttendees.length}
                    itemWidth={ITEM_ATTENDEE_WIDTH}
                    containerWidth={ITEM_ATTENDEE_WIDTH * 2.5}
                    renderActionButton={renderContactIcon}
                  />
                </Box>
                {otherCompanyAttendees.length === 1 && (
                  <AttendeeInfoContainer $disabled={otherCompanyAttendees?.[0]?.isDisabled}>
                    {!!otherCompanyAttendees[0].cardInfo.cardInfoTitle && (
                      <Typography variant="caption">
                        <TruncateLines lineHeight={1.2} lines={2}>
                          {otherCompanyAttendees[0].cardInfo.cardInfoTitle?.trim()}
                        </TruncateLines>
                      </Typography>
                    )}
                    {!!otherCompanyAttendees[0].cardInfo.cardInfoDescription && (
                      <Typography variant="body1">
                        <TruncateLines lineHeight={1.2} lines={2}>
                          {otherCompanyAttendees[0].cardInfo.cardInfoDescription?.trim()}
                        </TruncateLines>
                      </Typography>
                    )}
                  </AttendeeInfoContainer>
                )}
              </Box>
              <AttendeesArrow src={ArrowIcon} alt="arrow" $back={!meeting?.attendees?.otherCompany?.[0]?.isSender} />
              <Box display="flex">
                <Box minWidth={ITEM_ATTENDEE_WIDTH}>
                  <CardsList
                    cardsItems={companyAttendees}
                    totalCardsAmount={companyAttendees.length}
                    itemWidth={ITEM_ATTENDEE_WIDTH}
                    containerWidth={ITEM_ATTENDEE_WIDTH * 2.5}
                    renderActionButton={renderContactIcon}
                  />
                </Box>
                {companyAttendees.length === 1 && companyAttendees[0].globalCompanyId !== user?.companyId && (
                  <AttendeeInfoContainer $disabled={companyAttendees?.[0]?.isDisabled}>
                    {!!companyAttendees[0].cardInfo.cardInfoTitle && (
                      <Typography variant="caption">
                        <TruncateLines lineHeight={1.2} lines={2}>
                          {companyAttendees[0].cardInfo.cardInfoTitle?.trim()}
                        </TruncateLines>
                      </Typography>
                    )}
                    {!!companyAttendees[0].cardInfo.cardInfoDescription && (
                      <Typography variant="body1">
                        <TruncateLines lineHeight={1.2} lines={2}>
                          {companyAttendees[0].cardInfo.cardInfoDescription?.trim()}
                        </TruncateLines>
                      </Typography>
                    )}
                  </AttendeeInfoContainer>
                )}
              </Box>
            </AttendeesContainer>
          )
        )}

        {!hideDetailsBtn && (
          <DetailsButtonWrapper
            onKeyPress={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') {
                // @ts-expect-error onKeyPress is keyboard event handler and onDetailsClick expects mouse event
                onDetailsClick(event)
              }
            }}
          >
            <Box
              sx={{ cursor: 'pointer' }}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              height="20px"
              onClick={onDetailsClick}
            >
              <Typography variant="body1" sx={{ marginRight: '3px', fontWeight: '500' }}>
                {t(translationKeys.CONTACT_STATUS_DETAILS)}
              </Typography>
              <ExpandCircleDownIcon color="primary" />
            </Box>
          </DetailsButtonWrapper>
        )}
        {isImported && hideDetailsBtn ? (
          <ContactsOverviewCardLockIcon>
            <CustomTooltip
              title={
                isPrivateProfile
                  ? t(translationKeys.THIS_MEETING_WAS_IMPORTED)
                  : t(translationKeys.THIS_MEETING_WAS_IMPORTED_FOR_TEAM)
              }
              open={lockIconTooltipShown}
              onClose={() => setLockIconTooltipShown(false)}
              placement="top-end"
            >
              <LockIcon
                onMouseEnter={() => setLockIconTooltipShown(true)}
                onMouseLeave={() => setLockIconTooltipShown(false)}
                fontSize="small"
                htmlColor={colors.primary}
              />
            </CustomTooltip>
          </ContactsOverviewCardLockIcon>
        ) : null}
      </ContactsOverviewCardContent>
      {showSelection && (
        <ContactsRadioWrapper>
          <Radio sx={{ height: '40px', width: '40px' }} checked={isSelected} onChange={() => setSelected?.(meeting as Meeting)} />
        </ContactsRadioWrapper>
      )}
    </ContactsOverviewCardWrapper>
  )
}

export default memo(ContactsOverviewCard)
